import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Send, Trophy, Shield, Users, HeartHandshake, ExternalLink } from "lucide-react";
import { api, mediaUrl, formatError } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { SEO } from "@/components/SEO";
import { PageHero, SectionTitle } from "@/components/shared";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const BENEFITS = [
  { icon: Trophy, title: "Structured Development", text: "Clear rugby pathways from mini's to senior youth." },
  { icon: Shield, title: "Safe Environment", text: "Player welfare and safe technique come first." },
  { icon: Users, title: "Friendships", text: "A welcoming community for the whole family." },
  { icon: HeartHandshake, title: "Positive Coaching", text: "Encouraging coaches who build confidence." },
];

export default function Join() {
  const { settings } = useSettings();
  const [content, setContent] = useState({});
  const [groups, setGroups] = useState([]);
  const [form, setForm] = useState({ parent_name: "", email: "", phone: "", child_name: "", age_group: "", message: "", consent: false });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/public/page/join").then((r) => setContent(r.data?.content || {}));
    api.get("/public/age_groups").then((r) => setGroups(r.data));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) return toast.error("Please accept the privacy consent.");
    setSending(true);
    try {
      await api.post("/public/join", form);
      toast.success("Thank you! Your joining enquiry has been received.");
      setForm({ parent_name: "", email: "", phone: "", child_name: "", age_group: "", message: "", consent: false });
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const input = "w-full rounded-lg border border-slate-300 bg-white p-3 text-slate-900 placeholder:text-slate-400 focus:border-[#1E63D6] focus:outline-none focus:ring-2 focus:ring-[#1E63D6]/20";
  const regClosed = settings?.registration_status === "closed";

  return (
    <>
      <SEO title="Join BJRC | Brackenfell Junior Rugby Club" description="Join Brackenfell Junior Rugby Club. Enquire about junior rugby for your child in Brackenfell, Cape Town." />
      <PageHero title="Join BJRC" subtitle={content.welcome || "Welcome to the BJRC family! We'd love to tell you more about junior rugby at Brackenfell."} image={content.hero_image ? mediaUrl(content.hero_image) : undefined} />

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <SectionTitle center eyebrow="Why Join" title="The BJRC Advantage" />
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {BENEFITS.map((b) => (
            <div key={b.title} className="rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-sm">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-[#0B2A4A] text-[#E8A317]"><b.icon className="h-6 w-6" /></div>
              <h3 className="mt-4 font-heading text-lg font-bold uppercase text-[#0B2A4A]">{b.title}</h3>
              <p className="mt-2 text-sm text-slate-600">{b.text}</p>
            </div>
          ))}
        </div>
      </section>

      {settings?.join_form_url && (
        <section className="mx-auto max-w-5xl px-4 pb-4 sm:px-6 lg:px-8" data-testid="join-google-form-section">
          <SectionTitle center eyebrow="Register Online" title="Online Registration Form" subtitle="Complete our official registration form to sign your child up for the season." />
          <div className="mt-6 text-center">
            <a href={settings.join_form_url} target="_blank" rel="noreferrer" data-testid="join-google-form-button" className="inline-flex items-center gap-2 rounded-full bg-[#E8A317] px-8 py-3.5 font-extrabold uppercase tracking-wide text-[#0A1626] shadow-md transition-all hover:bg-[#CC8F12] active:scale-95">
              Open Registration Form <ExternalLink className="h-5 w-5" />
            </a>
          </div>
          <div className="mt-8 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <iframe
              title="BJRC Registration Form"
              src={`${settings.join_form_url}${settings.join_form_url.includes("?") ? "&" : "?"}embedded=true`}
              className="h-[1100px] w-full"
              style={{ border: 0 }}
              loading="lazy"
            >Loading…</iframe>
          </div>
        </section>
      )}

      <section className="mx-auto grid max-w-6xl gap-10 px-4 pb-16 sm:px-6 lg:grid-cols-2 lg:px-8">
        <div>
          <SectionTitle eyebrow="Enquire" title="Registration Enquiry" />
          {regClosed && <p className="mt-4 rounded-xl bg-amber-100 p-4 text-sm font-semibold text-amber-800">Registration is currently closed. Leave your details and we'll contact you when it reopens.</p>}
          <form onSubmit={submit} className="mt-6 rounded-2xl border border-slate-200 bg-white p-7 shadow-sm" data-testid="join-form">
            <div className="grid gap-4 sm:grid-cols-2">
              <input required placeholder="Parent/guardian name" className={input} value={form.parent_name} onChange={(e) => setForm({ ...form, parent_name: e.target.value })} data-testid="join-parent-name" />
              <input required type="email" placeholder="Email" className={input} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="join-email" />
              <input placeholder="Phone" className={input} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="join-phone" />
              <input placeholder="Child's name" className={input} value={form.child_name} onChange={(e) => setForm({ ...form, child_name: e.target.value })} data-testid="join-child-name" />
            </div>
            <div className="mt-4">
              <Select value={form.age_group} onValueChange={(v) => setForm({ ...form, age_group: v })}>
                <SelectTrigger className={input} data-testid="join-age-group"><SelectValue placeholder="Preferred age group" /></SelectTrigger>
                <SelectContent>{groups.map((g) => <SelectItem key={g.id} value={g.name}>{g.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <textarea placeholder="Anything you'd like us to know?" rows={4} className={`${input} mt-4`} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="join-message" />
            <label className="mt-4 flex items-start gap-3 text-sm text-slate-600">
              <Checkbox checked={form.consent} onCheckedChange={(v) => setForm({ ...form, consent: !!v })} data-testid="join-consent" />
              <span>I consent to BJRC storing these details to respond to my enquiry (POPIA). I understand no child's data is published publicly.</span>
            </label>
            <button disabled={sending} type="submit" data-testid="join-form-submit-button" className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-[#E8A317] py-3.5 font-extrabold uppercase text-[#0A1626] hover:bg-[#CC8F12] disabled:opacity-60">
              <Send className="h-5 w-5" /> {sending ? "Sending..." : "Submit Enquiry"}
            </button>
          </form>
        </div>

        <div>
          <SectionTitle eyebrow="Questions" title="Frequently Asked" />
          <Accordion type="single" collapsible className="mt-6 rounded-2xl border border-slate-200 bg-white px-5">
            {(content.faqs || []).map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-${i}`}>
                <AccordionTrigger className="text-left font-bold text-[#0B2A4A]">{f.q}</AccordionTrigger>
                <AccordionContent className="text-slate-600">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </>
  );
}
